import React, { useState } from 'react';
import "./TriggerStats.css";
import axios from "axios";
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { toast } from "../../components/Toast/toast.ts"


const TriggerStats = () => {
    const [loading, setLoading] = useState(false);
    const [triggerStats, setTriggerStats] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const [chartData, setChartData] = useState({});
    const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));

    // Watch for dark mode changes
    React.useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    const storedToken = localStorage.getItem("token");
    const fetchTriggerStats = async () => {
        if (storedToken) {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/cust-portal/push-button-report`,
                    {
                        headers: {
                            Authorization: `Bearer ${storedToken}`
                        }
                    })
                setTriggerStats(response?.data);
                setLoading(false);
            } catch (error) {
                toast.error(error?.response?.data?.message || error?.message || "An error occurred. Please try again.")
                setLoading(false);
            }
        }
    }

    React.useEffect(() => {
        fetchTriggerStats();
        // eslint-disable-next-line
    }, [])

    React.useEffect(() => {
        const textColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(14,14,16,0.5)';
        const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(14,14,16,0.08)';

        const weekLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        const fullLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const week1Data = fullLabels.map(day => triggerStats?.week1?.[day] || 0);
        const week2Data = fullLabels.map(day => triggerStats?.week2?.[day] || 0);

            const data = {
                labels: weekLabels,
                datasets: [
                    {
                        label: 'Last Week',
                        data: [...week1Data],
                        borderColor: 'rgba(184, 56, 56, 0.9)',
                        backgroundColor: (context: any) => {
                            const chart = context.chart;
                            const { ctx, chartArea } = chart;
                            if (!chartArea) return 'rgba(184, 56, 56, 0.1)';
                            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                            gradient.addColorStop(0, 'rgba(184, 56, 56, 0.25)');
                            gradient.addColorStop(1, 'rgba(184, 56, 56, 0.0)');
                            return gradient;
                        },
                        fill: true,
                        tension: 0.4,
                        pointRadius: 4,
                        pointBackgroundColor: 'rgba(184, 56, 56, 0.9)',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        borderWidth: 2,
                    },
                    {
                        label: 'Current Week',
                        data: [...week2Data],
                        borderColor: 'rgba(184, 137, 58, 0.9)',
                        backgroundColor: (context: any) => {
                            const chart = context.chart;
                            const { ctx, chartArea } = chart;
                            if (!chartArea) return 'rgba(184, 137, 58, 0.1)';
                            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                            gradient.addColorStop(0, 'rgba(184, 137, 58, 0.25)');
                            gradient.addColorStop(1, 'rgba(184, 137, 58, 0.0)');
                            return gradient;
                        },
                        fill: true,
                        tension: 0.4,
                        pointRadius: 4,
                        pointBackgroundColor: 'rgba(184, 137, 58, 0.9)',
                        pointBorderColor: '#fff',
                        pointBorderWidth: 2,
                        borderWidth: 2,
                    }
                ]
            };
            setChartData(data);

        const options = {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: textColor,
                        padding: 20,
                        font: { size: 11, family: 'Poppins' },
                        usePointStyle: true,
                        pointStyle: 'circle',
                    },
                },
                tooltip: {
                    backgroundColor: isDark ? 'rgba(20,20,26,0.95)' : 'rgba(255,255,255,0.95)',
                    titleColor: isDark ? '#fff' : '#111',
                    bodyColor: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                    borderWidth: 1,
                    padding: 10,
                    cornerRadius: 8,
                },
            },
            scales: {
                x: {
                    ticks: { color: textColor, font: { size: 11, family: 'Poppins' } },
                    grid: { display: false },
                    border: { display: false },
                },
                y: {
                    grid: { color: gridColor },
                    border: { display: false },
                    min: 0,
                    ticks: {
                        color: textColor,
                        font: { size: 11, family: 'Poppins' },
                    },
                },
            },
            layout: {
                padding: { top: 8, right: 8, bottom: 0, left: 0 },
            },
        };

        setChartOptions(options);
    }, [triggerStats, isDark]);

    return (
        <div className='h-full' >
            {
                loading ?
                    <div className="flex justify-center items-center h-[200px]">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" animationDuration=".5s" />
                    </div>
                    :
                    <div style={{ height: "100%", minHeight: "200px" }}>
                        <Chart type="line" data={chartData} options={chartOptions} />
                    </div>
            }
        </div>
    )
}

export default TriggerStats;